import { CustomerPrivacyContentSchema, CustomerPrivacyFieldsPatchSchema, } from './CustomerPrivacyNotice.js';
import { z } from 'zod';
import { FieldInitializationStateSchema, QuestionMediaSchema, QuestionTypeSchema, } from './common.js';
export const PARTICIPANT_EMAIL_HEADING_MAX_LENGTH = 100;
export const PARTICIPANT_EMAIL_BODY_MAX_LENGTH = 500;
/**
 * The participant email card copy in one of the study's other languages. The
 * heading/body settings hold it in the language the researcher wrote it in;
 * `origin` tells a researcher's own text from a machine translation of it.
 */
export const ParticipantEmailCopyTranslationSchema = z
    .object({
    heading: z.string().max(PARTICIPANT_EMAIL_HEADING_MAX_LENGTH).nullable(),
    body: z.string().max(PARTICIPANT_EMAIL_BODY_MAX_LENGTH).nullable(),
    origin: z.enum(['human', 'ai']),
})
    .strict();
const ParticipantEmailCopyLocaleSchema = z.string().trim().min(2).max(35);
export const ParticipantEmailCopyTranslationsSchema = z.record(ParticipantEmailCopyLocaleSchema, ParticipantEmailCopyTranslationSchema);
/** A settings patch merges per language: an entry replaces that language's copy, null removes it. */
export const ParticipantEmailCopyTranslationsPatchSchema = z.record(ParticipantEmailCopyLocaleSchema, ParticipantEmailCopyTranslationSchema.nullable());
export const PARTICIPANT_COPY_TEXT_MAX_LENGTH = 2000;
export const PARTICIPANT_COPY_TEXTS_MAX = 400;
/**
 * Every text a Clair conversation shows in one of the study's languages, by
 * key of @nava/participant-copy, as cs-listening-controller writes it: the
 * company's name filled in, an emptied text as '' (shown as nothing). Which
 * keys exist is the catalogue's, checked by its writer; research-data keeps
 * the texts as given, within these bounds.
 */
export const ParticipantCopyTextsSchema = z
    .record(z.string().trim().min(1).max(100), z.string().max(PARTICIPANT_COPY_TEXT_MAX_LENGTH))
    .refine((texts) => Object.keys(texts).length <= PARTICIPANT_COPY_TEXTS_MAX, {
    message: `At most ${PARTICIPANT_COPY_TEXTS_MAX} texts per language`,
});
/** The conversation's texts per locale of the study; `{}` for a study that shows the app's own. */
export const ParticipantCopyByLocaleSchema = z.record(ParticipantEmailCopyLocaleSchema, ParticipantCopyTextsSchema);
/** A settings patch merges per locale: an entry replaces that locale's texts, null removes them. */
export const ParticipantCopyByLocalePatchSchema = z.record(ParticipantEmailCopyLocaleSchema, ParticipantCopyTextsSchema.nullable());
export const CLAIR_BRIEF_MAX_LENGTH = 500_000;
/**
 * What a Clair program tells the voice agents of a conversation's study beyond
 * its content (ClairConversationBrief of @nava/elevenlabs-facade-contracts):
 * written by cs-listening-controller, passed on by project-publishing-controller
 * and participant-bff to every agent made for the study. research-data keeps it
 * as given, an object of at most this many characters as JSON: its shape is
 * checked by its writer and its readers, so it can grow without research-data.
 */
export const ClairBriefSchema = z
    .record(z.string(), z.unknown())
    .refine((brief) => JSON.stringify(brief).length <= CLAIR_BRIEF_MAX_LENGTH, {
    message: `At most ${CLAIR_BRIEF_MAX_LENGTH} characters as JSON`,
});
/**
 * How many finished conversations one participant's personal link takes: one
 * (`once`, the link then shows that it was used) or more (`repeat`, up to the
 * cap participant-bff keeps). A finished conversation is one that passed the
 * automatic quality check. cs-listening-controller writes `repeat` at a Clair
 * conversation's approval; every other study keeps `once`. cs-programs-data,
 * cs-pulses-data and cs-listening-controller declare the same enum.
 */
export const ParticipantLinkUseSchema = z.enum(['once', 'repeat']);
export const AssistantTalkingSpeedSchema = z.number().min(-1).max(1);
export const ParticipantRecordingPrivacyModeSchema = z.enum([
    'transparent',
    'obfuscated',
    'hidden',
]);
export const ResolvedProjectQuestionSchema = z
    .object({
    id: z.string().guid(),
    type: QuestionTypeSchema,
    text: z.string(),
    options: z.array(z.string()).optional(),
    required: z.boolean(),
    followUpCount: z.number().multipleOf(1).nonnegative(),
    followUpGuidance: z.string().optional(),
    theme: z.string().optional(),
    media: QuestionMediaSchema.optional(),
})
    .strict();
export const ResolvedProjectContentSchema = z
    .object({
    objective: z.string().nullable(),
    questions: z.array(ResolvedProjectQuestionSchema),
    opening: z.string().nullable(),
    closing: z.string().nullable(),
    participantHeading: z.string().nullable(),
    participantTitle: z.string().nullable(),
    participantDescription: z.string().nullable(),
    targetGroup: z.string().nullable(),
    estimatedDurationMinutes: z.number().multipleOf(1).nonnegative(),
})
    .strict();
export const ProjectSettingsPatchRequestSchema = z
    .object({
    assistantTalkingSpeed: AssistantTalkingSpeedSchema.optional(),
    participantRecordingPrivacyMode: ParticipantRecordingPrivacyModeSchema.optional(),
    collectParticipantEmail: z.boolean().optional(),
    participantEmailHeading: z
        .string()
        .max(PARTICIPANT_EMAIL_HEADING_MAX_LENGTH)
        .nullable()
        .optional(),
    participantEmailBody: z.string().max(PARTICIPANT_EMAIL_BODY_MAX_LENGTH).nullable().optional(),
    participantEmailCopyTranslations: ParticipantEmailCopyTranslationsPatchSchema.optional(),
    participantCopy: ParticipantCopyByLocalePatchSchema.optional(),
    /** Replaces the brief; null removes it. */
    clairBrief: ClairBriefSchema.nullable().optional(),
    customerPrivacyFields: CustomerPrivacyFieldsPatchSchema.optional(),
    customerPrivacyAuthor: z
        .object({
        actorType: z.literal('user'),
        actorId: z.string().min(1),
        origin: z.literal('user'),
    })
        .strict()
        .optional(),
    allowRememberParticipantConsent: z.boolean().optional(),
    skipParticipantLandingPage: z.boolean().optional(),
    /** Omitted keeps it; a study that never set it reads `once`. */
    participantLinkUse: ParticipantLinkUseSchema.optional(),
    allowParticipantChat: z.boolean().optional(),
    fieldInitializationState: FieldInitializationStateSchema.optional(),
    organisationName: z.string().nullable().optional(),
    previewCompleted: z.boolean().optional(),
})
    .strict();
export const ProjectSettingsResponseSchema = z
    .object({
    id: z.string(),
    projectId: z.string(),
    version: z.number().multipleOf(1).nonnegative(),
    assistantTalkingSpeed: AssistantTalkingSpeedSchema.prefault(0),
    participantRecordingPrivacyMode: ParticipantRecordingPrivacyModeSchema.prefault('transparent'),
    collectParticipantEmail: z.boolean().prefault(false),
    participantEmailHeading: z.string().nullable().optional(),
    participantEmailBody: z.string().nullable().optional(),
    participantEmailCopyTranslations: ParticipantEmailCopyTranslationsSchema.prefault({}),
    participantCopy: ParticipantCopyByLocaleSchema.prefault({}),
    clairBrief: ClairBriefSchema.nullable().optional(),
    customerPrivacyContent: CustomerPrivacyContentSchema.optional(),
    allowRememberParticipantConsent: z.boolean().prefault(true),
    skipParticipantLandingPage: z.boolean().prefault(false),
    participantLinkUse: ParticipantLinkUseSchema.prefault('once'),
    allowParticipantChat: z.boolean().prefault(false),
    fieldInitializationState: FieldInitializationStateSchema,
    organisationName: z.string().nullable().optional(),
    previewCompleted: z.boolean(),
    moderatorIds: z.array(z.string().trim().min(1)).prefault([]),
    localeModerators: z
        .array(z
        .object({
        locale: z.string().trim().min(2),
        isDefault: z.boolean(),
        moderatorId: z.string().trim().min(1),
    })
        .strict())
        .prefault([]),
    createdAt: z.string().datetime(),
})
    .strict();
//# sourceMappingURL=Config.js.map